import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Snowflake, Wind, PackageSearch, DollarSign, GraduationCap, BookOpen, Compass } from 'lucide-react';
import { api, assetUrl } from '../lib/api';
import CTASection from '../components/CTASection';
import useSEO from '../hooks/useSEO';

const ICONS = { machines: Compass, nozzles: PackageSearch, air: Wind, 'dry-ice': Snowflake, costs: DollarSign, training: GraduationCap };

const KnowledgeCenter = () => {
    const [guides, setGuides] = useState(null);
    useSEO({
        title: 'Knowledge Center — Dry Ice Blasting Buying Guides | Dry Ice Bulls',
        description: 'Buying guides for dry ice blasting machines, nozzles, compressed air, dry ice supply, running costs and operator training.',
    });
    useEffect(() => { api.get('/guides').then((r) => setGuides(r.data)).catch(() => setGuides([])); }, []);

    return (
        <div className="pt-24 pb-24" data-testid="knowledge-page">
            <div className="container-page">
                <div className="label-caps text-brand-primary mb-4">/ Knowledge Center</div>
                <h1 className="font-display font-black uppercase text-5xl md:text-7xl leading-[0.95] text-neutral-900">
                    Buy it right.<br /><span className="text-brand-primary">Run it right.</span>
                </h1>
                <p className="mt-6 max-w-3xl text-base md:text-lg text-neutral-600 leading-relaxed">
                    Practical buying guides on machines, nozzles, air supply, dry ice and running costs — from people who use this equipment every day.
                </p>
                <Link to="/machine-finder" className="mt-8 inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-bold px-6 py-3.5 label-caps transition-colors" data-testid="kc-finder-cta">
                    Not sure where to start? Take the Machine Finder <ArrowRight className="h-4 w-4" />
                </Link>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kc-guides">
                    {guides === null && <div className="text-neutral-400 label-caps">Loading…</div>}
                    {guides?.length === 0 && <div className="text-neutral-500" data-testid="kc-empty">Guides coming soon.</div>}
                    {guides?.map((g) => {
                        const Icon = ICONS[g.category] || BookOpen;
                        return (
                            <Link key={g.id} to={`/knowledge/${g.slug}`} className="group border border-neutral-200 bg-white hover:border-brand-primary transition-colors flex flex-col" data-testid={`kc-${g.slug}`}>
                                {g.cover_image && <img src={assetUrl(g.cover_image)} alt="" loading="lazy" className="w-full aspect-[16/9] object-cover border-b border-neutral-200" />}
                                <div className="p-6 flex-1 flex flex-col">
                                    <div className="inline-flex items-center justify-center w-12 h-12 border border-neutral-200 group-hover:border-brand-primary mb-4">
                                        <Icon className="h-5 w-5 text-brand-primary" />
                                    </div>
                                    <h3 className="font-display font-bold uppercase text-xl text-neutral-900 leading-tight group-hover:text-brand-primary transition-colors">{g.title}</h3>
                                    <p className="mt-3 text-sm text-neutral-600 leading-relaxed flex-1">{g.excerpt}</p>
                                    <div className="mt-4 inline-flex items-center gap-1.5 label-caps text-brand-primary text-xs">Read guide <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" /></div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>

            <CTASection variant="knowledge" heading="Ready to talk shop?" subheading="Ask about your application and get a clear answer — text or WhatsApp us anytime."
                context={{ trackingLocation: 'knowledge_bottom', subject: 'Knowledge Center — specialist question' }} testid="knowledge-cta-bottom" />
        </div>
    );
};

export default KnowledgeCenter;
